/**
 * useIsAdmin — Fetches /api/me once per session and shares the user's flags.
 * Returns { isAdmin, isWmp, loading, failed } so components can conditionally render
 * admin features and WMP-only sections (Fund). Both flags are true in demo mode.
 * The server enforces both; these flags only decide what to show.
 *
 * Waits for MSAL to finish (no interaction in progress, an account signed in): right after
 * a login redirect the token isn't available yet, and fetching then would fail. Failures are
 * not cached — the next mount retries — and every caller shares one in-flight request.
 */

import { useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { getUserInfo, isDemoMode } from "../utils/PowerBIApiCall";

type Flags = { isAdmin: boolean; isWmp: boolean };
const NONE: Flags = { isAdmin: false, isWmp: false };
const DEMO: Flags = { isAdmin: true, isWmp: true };

let cached: Flags | null = null;
let inflight: Promise<Flags> | null = null;

function loadFlags(): Promise<Flags> {
  inflight ??= getUserInfo()
    .then(
      (info) =>
        (cached = { isAdmin: info.isAdmin, isWmp: info.isWmp === true }),
    )
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useIsAdmin(): Flags & { loading: boolean; failed: boolean } {
  const demo = isDemoMode();
  // Outside a MsalProvider (demo mode) this is msal-react's stub context — harmless.
  const { inProgress, accounts } = useMsal();
  const ready = inProgress === InteractionStatus.None && accounts.length > 0;
  const [flags, setFlags] = useState<Flags | null>(demo ? DEMO : cached);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (demo || !ready) return;
    if (cached) {
      setFlags(cached); // another caller finished first
      return;
    }
    let live = true;
    loadFlags()
      .then((f) => live && setFlags(f))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [demo, ready]);

  return { ...(flags ?? NONE), loading: flags === null && !failed, failed };
}
